/**
 * A single Conversation Triage category — a reviewer-assigned category tag on a
 * conversation. Modeled as a group so a future category -> subvalues hierarchy
 * can nest under it without changing the filter shape.
 */
export interface ConversationTriageCategory {
  label: string
  simulationOutputIds: string[]
  count: number
}

/** Visibility keyed by category label; a missing key means visible. */
export type ConversationTriageVisibility = Record<string, boolean>

export function isTriageCategoryVisible(
  visibility: ConversationTriageVisibility,
  label: string,
): boolean {
  return visibility[label] ?? true
}

interface TriageFilterInput {
  categories: ConversationTriageCategory[]
  visibility: ConversationTriageVisibility
}

/**
 * The simulation ids to scope the report to, or null when no category is hidden
 * (no filter -> show all conversations, tagged or not). When some categories are
 * hidden, returns the deduped union of the still-visible categories' sims; an
 * empty array then means "every visible category is empty -> show nothing".
 */
export function resolveTriageFilterSimIds({
  categories,
  visibility,
}: TriageFilterInput): string[] | null {
  const anyHidden = categories.some(
    (category) => !isTriageCategoryVisible(visibility, category.label),
  )
  if (!anyHidden) {
    return null
  }
  const result = new Set<string>()
  for (const category of categories) {
    if (isTriageCategoryVisible(visibility, category.label)) {
      for (const id of category.simulationOutputIds) {
        if (id) {
          result.add(id)
        }
      }
    }
  }
  return [...result]
}
